import React from 'react';
import { SimulationConfig } from '@/types';
import { 
  FileAudio, 
  RefreshCw, 
  Zap,
  Info
} from 'lucide-react';

import { InfoPopup } from './InfoPopup';

interface SongConverterSectionProps {
  config: SimulationConfig;
  onFileUpload: (e: React.ChangeEvent<HTMLInputElement>) => void;
  isAnalyzing: boolean;
  analysisProgress: number;
}

export const SongConverterSection: React.FC<SongConverterSectionProps> = ({ config, onFileUpload, isAnalyzing, analysisProgress }) => (
  <div className={`p-8 rounded-[2.5rem] border shadow-sm transition-colors duration-300 ${config.darkMode ? 'bg-white/5 border-white/5' : 'bg-zinc-50 border-zinc-950/5'}`}>
    <div className="flex items-center gap-3 mb-8 px-1">
      <div className="w-8 h-8 rounded-xl bg-neon-cyan/20 flex items-center justify-center shadow-lg border border-cyan-500/20">
        <Zap className="w-4 h-4 text-cyan-500" />
      </div>
      <div className="flex items-center gap-2">
        <h3 className="text-[10px] font-display font-black uppercase tracking-[0.2em] opacity-40">Song to Melody Converter</h3>
        <InfoPopup settingKey="neuralAnalysisHub" darkMode={config.darkMode} />
      </div>
    </div>

    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-3">
        <span className="text-[9px] font-display font-black uppercase tracking-[0.2em] opacity-30 ml-1">Audio Analysis Engine</span>
        <label className={`w-full h-20 rounded-[1.5rem] border-2 border-dashed border-zinc-950/10 cursor-pointer flex flex-col items-center justify-center gap-2 transition-all ${config.darkMode ? 'bg-black/30 border-white/10 hover:bg-white/5 text-white/40' : 'bg-white hover:bg-zinc-50 text-zinc-400 shadow-inner'}`}>
          {isAnalyzing ? <RefreshCw className="w-5 h-5 animate-spin" /> : <FileAudio className="w-5 h-5" />}
          <span className="text-[10px] font-display font-black uppercase tracking-widest">
            {isAnalyzing ? `Extracting Notes... ${analysisProgress}%` : 'Upload Song (MP3/WAV)'}
          </span>
          <input type="file" accept="audio/*" onChange={onFileUpload} className="hidden" disabled={isAnalyzing} />
        </label>
      </div>

      <div className="flex items-start gap-4 p-5 rounded-2xl border bg-cyan-500/5 border-cyan-500/20 shadow-sm">
        <div className="p-3 rounded-xl bg-neon-cyan/20 text-cyan-500 shadow-md border border-cyan-500/20">
          <Info className="w-4 h-4" />
        </div>
        <div className="flex flex-col gap-1">
          <span className="text-[9px] font-black uppercase tracking-widest text-zinc-950/80">Audio Slicing</span>
          <p className="text-[10px] font-display font-bold text-zinc-400 leading-relaxed uppercase tracking-widest">
            The system will slice your audio into 16 unique musical segments. These will play when balls collide with the boundaries.
          </p>
        </div>
      </div>
    </div>
  </div>
);
